import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquareWarning, ClipboardList, Mail, Smartphone, ExternalLink,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { dateTime, humanise } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, EmptyState, Field, LoadingBlock, Select,
  Tabs, Textarea, useToast, cn,
} from '@/components/ui';
import type {
  AdminFeedbackRow, PlatformTask, FeedbackStatus, PlatformTaskStatus,
} from '@shared/types';

/**
 * The two queues that are somebody's job.
 *
 * Everything else in this console answers a question — who is on a trial, what
 * did we send them. These two are work: a customer waiting on a phone number
 * cannot fully use what they signed up for, and a bug report nobody reads for
 * a week teaches people to stop sending them.
 *
 * Both were readable from the Apps Script editor before this screen existed,
 * which is fine for one person and stops working the moment there are two.
 */

const FEEDBACK_STATUSES: { value: FeedbackStatus; label: string; tone: any }[] = [
  { value: 'new', label: 'Received', tone: 'info' },
  { value: 'seen', label: 'Read', tone: 'neutral' },
  { value: 'planned', label: 'Planned', tone: 'brand' },
  { value: 'done', label: 'Done', tone: 'ok' },
  { value: 'declined', label: 'Not planned', tone: 'neutral' },
];

const KIND_TONE: Record<string, any> = {
  bug: 'danger', idea: 'brand', question: 'info', other: 'neutral',
};

export default function Inbox() {
  const [tab, setTab] = useState('feedback');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-extrabold tracking-tight">Inbox</h1>
        <p className="text-sm text-muted mt-1">
          What customers have told us, and what we still owe them.
        </p>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'feedback', label: 'Feedback' },
          { id: 'tasks', label: 'Jobs' },
        ]}
      />

      {tab === 'feedback' ? <FeedbackQueue /> : <TaskQueue />}
    </div>
  );
}

function FeedbackQueue() {
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const { data, loading, reload } = useApi<{
    feedback: AdminFeedbackRow[];
    counts: Record<string, number>;
  }>('/admin/feedback', { status: filter || undefined });

  const rows = data?.feedback ?? [];

  async function setStatus(row: AdminFeedbackRow, status: FeedbackStatus) {
    setBusy(true);
    try {
      await api.patch(`/admin/feedback/${row.id}`, {
        status,
        // Only send the note when this row is the one open, so changing a
        // status from the list cannot wipe a note somebody wrote earlier.
        operator_note: openId === row.id ? note : undefined,
      });
      toast.success('Updated. The customer sees this in their Help screen.');
      setOpenId(null);
      setNote('');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update that report');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !data) return <LoadingBlock rows={5} />;

  return (
    <Card className="p-0 overflow-hidden">
      <CardHeader
        title="Feedback"
        subtitle={data ? `${data.counts.new} unread · ${data.counts.planned} planned` : undefined}
        action={
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-auto min-w-[150px]"
          >
            <option value="">Everything</option>
            {FEEDBACK_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
        }
      />

      {rows.length === 0 ? (
        <div className="p-6">
          <EmptyState
            icon={<MessageSquareWarning className="h-6 w-6" />}
            title="Nothing here"
            message="Reports from managers and tenants land here the moment they are sent."
          />
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((row) => {
            const open = openId === row.id;
            return (
              <li key={row.id} className="p-4 sm:p-5 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={KIND_TONE[row.kind] ?? 'neutral'}>{humanise(row.kind)}</Badge>
                  <Badge tone={FEEDBACK_STATUSES.find((s) => s.value === row.status)?.tone}>
                    {FEEDBACK_STATUSES.find((s) => s.value === row.status)?.label ?? row.status}
                  </Badge>
                  <span className="text-sm font-medium min-w-0 truncate">{row.subject}</span>
                  <span className="text-xs text-faint ml-auto whitespace-nowrap">
                    {dateTime(row.created_at)}
                  </span>
                </div>

                <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">{row.body}</p>

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-faint">
                  <span>
                    {row.author_label ?? 'someone'} ({row.author_type})
                    {row.org_name && (
                      <>
                        {' at '}
                        {row.org_id ? (
                          <Link
                            to={`/admin/organisations/${row.org_id}`}
                            className="text-brand hover:underline"
                          >
                            {row.org_name}
                          </Link>
                        ) : row.org_name}
                      </>
                    )}
                  </span>
                  {row.page_path && <span>screen: {row.page_path}</span>}
                  {row.reply_to && (
                    <a href={`mailto:${row.reply_to}`} className="hover:text-ink">
                      reply to {row.reply_to}
                    </a>
                  )}
                  {row.user_agent && (
                    <span className="truncate max-w-[320px]" title={row.user_agent}>
                      {row.user_agent}
                    </span>
                  )}
                </div>

                {row.operator_note && !open && (
                  <p className="text-sm border-l-2 border-brand/40 pl-3">{row.operator_note}</p>
                )}

                {open ? (
                  <div className="space-y-3">
                    <Field
                      label="What should we tell them?"
                      hint="Shown to whoever filed it, in their own Help screen."
                    >
                      <Textarea
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Fixed in the next release."
                      />
                    </Field>
                    <div className="flex flex-wrap gap-2">
                      {FEEDBACK_STATUSES.map((s) => (
                        <Button
                          key={s.value}
                          size="sm"
                          variant={s.value === row.status ? 'primary' : 'secondary'}
                          loading={busy}
                          onClick={() => void setStatus(row, s.value)}
                        >
                          {s.label}
                        </Button>
                      ))}
                      <Button size="sm" onClick={() => { setOpenId(null); setNote(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => { setOpenId(row.id); setNote(row.operator_note ?? ''); }}
                  >
                    Reply and set status
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function TaskQueue() {
  const toast = useToast();
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const { data, loading, reload } = useApi<{ tasks: PlatformTask[]; sms_policy: string }>(
    '/admin/tasks', { include_done: showDone ? 'true' : undefined },
  );

  const tasks = data?.tasks ?? [];

  async function move(task: PlatformTask, status: PlatformTaskStatus) {
    setBusy(task.id);
    try {
      await api.patch(`/admin/tasks/${task.id}`, { status });
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update that job');
    } finally {
      setBusy(null);
    }
  }

  if (loading && !data) return <LoadingBlock rows={4} />;

  return (
    <div className="space-y-4">
      <Card className="p-0 overflow-hidden">
        <CardHeader
          title="Jobs"
          subtitle={
            data?.sms_policy === 'per_org'
              ? 'Every signup needs a number of its own'
              : 'Everyone shares one number, so only sending addresses are raised'
          }
          action={
            <Button size="sm" onClick={() => setShowDone((v) => !v)}>
              {showDone ? 'Hide finished' : 'Show finished'}
            </Button>
          }
        />

        {tasks.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={<ClipboardList className="h-6 w-6" />}
              title="Nothing outstanding"
              message="Every organisation is fully set up."
            />
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {tasks.map((task) => (
              <li key={task.id} className="p-4 sm:p-5 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="shrink-0 text-faint" aria-hidden>
                    {task.kind === 'assign_sms_number'
                      ? <Smartphone className="h-4 w-4" />
                      : <Mail className="h-4 w-4" />}
                  </span>
                  <span className={cn('text-sm font-medium', task.status === 'done' && 'text-muted line-through')}>
                    {task.title}
                  </span>
                  <Badge tone={task.status === 'open' ? 'warn' : task.status === 'done' ? 'ok' : 'neutral'}>
                    {humanise(task.status)}
                  </Badge>
                  <span className="text-xs text-faint ml-auto whitespace-nowrap">
                    {dateTime(task.created_at)}
                  </span>
                </div>

                {task.detail && (
                  <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">
                    {task.detail}
                  </p>
                )}
                {task.note && <p className="text-sm text-muted">{task.note}</p>}

                <div className="flex flex-wrap items-center gap-2">
                  {task.org_id && (task.status === 'open' || task.status === 'in_progress') && (
                    <Link to={`/admin/organisations/${task.org_id}`}>
                      <Button size="sm" variant="primary" icon={<ExternalLink className="h-3.5 w-3.5" />}>
                        {task.kind === 'assign_sms_number' ? 'Assign a number' : 'Assign an address'}
                      </Button>
                    </Link>
                  )}
                  {task.org_id && task.status !== 'open' && task.status !== 'in_progress' && (
                    <Link to={`/admin/organisations/${task.org_id}`}>
                      <Button size="sm" icon={<ExternalLink className="h-3.5 w-3.5" />}>
                        Open {task.org_name}
                      </Button>
                    </Link>
                  )}
                  {(task.status === 'open' || task.status === 'in_progress') && (
                    <>
                      {task.status === 'open' && (
                        <Button
                          size="sm"
                          loading={busy === task.id}
                          onClick={() => void move(task, 'in_progress')}
                        >
                          I am on it
                        </Button>
                      )}
                      <Button
                        size="sm"
                        loading={busy === task.id}
                        onClick={() => void move(task, 'cancelled')}
                      >
                        Not needed
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/*
        No instructions card. Assigning used to mean the Apps Script editor,
        which was never a good answer — it is one row in a sheet, and the
        validation that made the editor version safe now lives in the route
        that the console calls. The link goes where the work happens.
      */}
    </div>
  );
}
